import type { ApiResponse } from "../types/dto";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? "").replace(/\/+$/, "");

// Every mutating call needs the CSRF header while cookie-authenticated; GET never does. Sending the
// header on the handful of pre-session mutating endpoints (register/login/verify-email/forgot-password/
// reset-password) is harmless — they don't check it — so this stays a blanket rule instead of a
// per-path allowlist ("simplest correct behavior is acceptable" per spec).
const MUTATING_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export class DeveloperApiError extends Error {
  readonly code: string | null | undefined;
  readonly status: number;
  readonly traceId: string;

  constructor(code: string | null | undefined, message: string, status: number, traceId: string) {
    super(message);
    this.code = code;
    this.status = status;
    this.traceId = traceId;
  }
}

// Module-level (not React state) so any call site can read/write the latest CSRF token without
// importing the auth context — mirrors why admin/auth/tokenStore.ts is a plain module rather than
// context state. DeveloperAuthProvider is the only writer: it calls setCsrfToken() after every
// GET /auth/me and registers setCsrfRefresher() for the stale-token retry path below.
//
// This module has zero import of anything under admin/ — the developer and admin auth domains are
// kept completely independent, so a 401 here can never touch the admin's bearer-token session.
let csrfToken: string | null = null;
let csrfRefresher: (() => Promise<string | null>) | null = null;
const unauthorizedListeners = new Set<() => void>();

export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

export function getCsrfToken(): string | null {
  return csrfToken;
}

// DeveloperAuthProvider registers its refresh() here so a stale-CSRF response (403
// CSRF_TOKEN_INVALID) can recover with a single re-fetch-and-retry instead of forcing a reload.
export function setCsrfRefresher(refresher: (() => Promise<string | null>) | null): void {
  csrfRefresher = refresher;
}

// A 401 from any /api/developer/* call means "not logged in" for THIS module only.
// DeveloperAuthProvider subscribes to flip its own status to "anonymous" and redirect to
// /developer/login — this never touches admin auth state.
export function onUnauthorized(listener: () => void): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

async function send<T>(method: string, path: string, body: unknown, allowCsrfRetry: boolean): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (MUTATING_METHODS.has(method) && csrfToken) headers["X-CSRF-Token"] = csrfToken;

  const response = await fetch(`${BASE_URL}${path}`, {
    method,
    headers,
    // Required so the developer session cookie round-trips — same-origin in production via the
    // Worker (mostly a no-op there), but necessary for correctness in any cross-origin dev setup.
    credentials: "include",
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (response.status === 401) {
    unauthorizedListeners.forEach((listener) => listener());
  }

  let parsed: ApiResponse<T> | null = null;
  try {
    parsed = (await response.json()) as ApiResponse<T>;
  } catch {
    // Non-JSON body (e.g. an upstream 502) — fall through to the generic error below.
  }

  if (!parsed || !parsed.success) {
    const code = parsed?.code;
    if (code === "CSRF_TOKEN_INVALID" && allowCsrfRetry && csrfRefresher) {
      const refreshed = await csrfRefresher();
      if (refreshed) {
        csrfToken = refreshed;
        return send<T>(method, path, body, false);
      }
    }
    throw new DeveloperApiError(
      code,
      parsed?.message ?? `Request failed (HTTP ${response.status}).`,
      response.status,
      parsed?.traceId ?? "unknown",
    );
  }

  return parsed.data as T;
}

export const developerApiClient = {
  get: <T>(path: string) => send<T>("GET", path, undefined, false),
  post: <T>(path: string, body?: unknown) => send<T>("POST", path, body, true),
  put: <T>(path: string, body?: unknown) => send<T>("PUT", path, body, true),
  delete: <T>(path: string) => send<T>("DELETE", path, undefined, true),
};
